import {
  featuresReducer,
  findPhotosByUnit,
  findUnit,
  findBaseUnit,
  translationFromLocale,
  rulesReducer,
  getFeatureWithAssertedIcon
} from './index';
import Locales from '@spa/shared/constants/locales';
import { getAmount } from './offer';
import { UNIT_TYPES_RENT } from '@spa/shared/constants/unitTypes';
import { PRIVATE_FEATURES, SERVICES_FEATURES } from '@spa/shared/constants/services-features';
import { isBed, isWcOrBathroom } from '@spa/shared/utils/unitTypes';
import { UNIT_TYPE } from '@spa/shared/constants/typology';
import { joinTranslations } from '../stringManipulation';
import { PROPERTY_RULES } from '@spa/shared/constants/property-rules';
import { RESTRICTIONS_CODES_MAPPER } from '@spa/shared/constants/restrictionsCodes';

const SHARED_UNITS = ['kitchen', 'bathroom', 'living-room'];
const ROOT_UNITS = ['studio', 'apartment', 'bedroom'];

export const cityFromLanguage = (property, language = 'en') => {
  const locale = Locales[language.toUpperCase()];

  return translationFromLocale(property.globalization_aggregate.city.metadata.name_translations, locale);
};

export const getCitySlug = property => property.globalization_aggregate.city.configuration.slug;

export const descriptionFromLanguage = ({ metadata = [] }, language = 'en') => {
  const locale = Locales[language.toUpperCase()];

  return translationFromLocale(metadata, locale);
};

export const titleFromLanguage = ({ title_metadata = [] }, language = 'en') => {
  const locale = Locales[language.toUpperCase()];

  return translationFromLocale(title_metadata, locale);
};

export const neighborhoodFromLanguage = (property, language = 'en') => {
  const locale = Locales[language.toUpperCase()];

  if (!property.neighborhood) {
    return '';
  }

  return translationFromLocale(property.neighborhood.metadata.name_translations, locale);
};

export const unitFromLanguage = (unit, language = 'en') => {
  const locale = Locales[language.toUpperCase()];

  return translationFromLocale(unit.metadata.title_translations, locale);
};

export const neighborhoodDescriptionTranslation = (
  {
    neighborhood_page: {
      metadata: {
        description_translations: { items }
      }
    }
  },
  language = 'en'
) => {
  const locale = Locales[language.toUpperCase()];

  return translationFromLocale(items, locale);
};

export const getVerificationsFromProperty = ({ verification, accommodation_provider, offers }) => {
  const trusted = accommodation_provider.verifications.trusted || false;
  const checked = verification.verified || false;
  const allExclusive = offers.every(obj => obj.accommodation_offer.contract.exclusive === true) || false;

  return { checked, trusted, allExclusive };
};

export const getOffersTypesKeys = ({ offers = [], floors = [], isSearch = false }) =>
  offers.reduce((acc, offer) => {
    const unitId = offer.accommodation_offer?.property?.unit_id;
    const units = floors[0]?.units;
    const unit = units?.find(unit => findBaseUnit(unit, unitId)) || {};
    const unitRentType = isSearch ? offer.property.rent_by : UNIT_TYPES_RENT[unit.type_code];
    const type = isSearch ? `results.residence.rent_by.${unitRentType}` : `subheader.${unitRentType}`;

    if (acc.includes(type) || !unitRentType) {
      return acc;
    }

    return [...acc, type];
  }, []);

export const getOffersTypesDescription = (t, offersTypesKeys = [], suffixKey, andKey) => {
  if (!offersTypesKeys.length) {
    return;
  }
  const translatedOffersTypes = offersTypesKeys.map(offerType => t(offerType));

  return `${joinTranslations(t, translatedOffersTypes, t(andKey)).trim()} ${t(suffixKey)}`;
};

export const getLowestOfferPrice = ({ offers }) => {
  const offer = offers.reduce(
    (acc, offer) => (!Object.keys(acc).length || getAmount(acc) > getAmount(offer) ? offer : acc),
    {}
  );

  return offer.accommodation_offer.reference_price;
};

export const transformBuildingUnitFeatures = ({ floors: [{ units }], all_photos: allPhotos }) => {
  const building = units.find(unit => unit.type_code === 'building');
  if (!building) {
    return { units: [], features: [] };
  }

  const buildingFeatures = (building.features || []).reduce((acc, feature) => featuresReducer(acc, feature), []);
  const baseAcc = { units: [], features: { building: buildingFeatures } };

  return (building.subunits || []).reduce((acc, subunit) => {
    const photos = findPhotosByUnit(subunit, allPhotos);

    const features = (subunit.features || []).reduce((acc, feature) => featuresReducer(acc, feature), []);
    const serviceFeature = SERVICES_FEATURES?.[subunit.type_code];
    const feature = serviceFeature ? [{ ...serviceFeature, exists: true }] : [];

    const {
      features: { building: buildingFeatures, ...featuresRest }
    } = acc;

    return {
      units: !photos.length ? acc.units : [...acc.units, { ...subunit, photos }],
      features: { building: [...buildingFeatures, ...feature], ...featuresRest, [subunit.type_code]: features }
    };
  }, baseAcc);
};

export const getRulesFromProperty = property => {
  const { rules } = property;

  return rules ? rules.reduce(rulesReducer, []) : [];
};

export const backwardCompatibility = property => {
  return property.offers.reduce(
    (acc, offer) => {
      if (offer.accommodation_offer.property.subunit_id || offer.accommodation_offer.property.unit_id) {
        return { ...acc, offers: [...acc.offers, offer] };
      }

      return {
        ...acc,
        floors: [
          {
            number: 0,
            units: [
              {
                id: offer.accommodation_offer.id,
                type_code: property.typology.type_code,
                photos: acc.photos,
                subunits: [...acc.floors[0].units.filter(unit => unit.type_code !== 'building')]
              }
            ]
          }
        ],
        offers: [
          ...acc.offers,
          {
            ...offer,
            accommodation_offer: {
              ...offer.accommodation_offer,
              property: {
                ...offer.accommodation_offer.property,
                unit_id: offer.accommodation_offer.id
              }
            }
          }
        ]
      };
    },
    { ...property, offers: [] }
  );
};

export const getFeatures = (unitId, { floors: [{ units }] }) => {
  const unit = findUnit(units, unitId);
  const baseUnit = units.find(rootUnit => findBaseUnit(rootUnit, unitId));
  if (!unit || !baseUnit) {
    return { privateFeatures: null, commonFeatures: null, mainFeatures: null };
  }

  const unitsMissing = SHARED_UNITS.filter(
    unitType => baseUnit.subunits && baseUnit.subunits.find(subunit => subunit.type_code === unitType)
  );

  const sharedUnits = unitsMissing.reduce((acc, missingTypeCode) => {
    const unitFound = units.find(unit => unit.type_code === missingTypeCode);
    return unitFound ? [...acc, unitFound] : acc;
  }, []);

  const commonFeatures = isSameBaseUnit(unit, baseUnit)
    ? []
    : getFeaturesFromUnit(
        {
          ...baseUnit,
          subunits: [...baseUnit.subunits.filter(subunit => SHARED_UNITS.includes(subunit.type_code)), ...sharedUnits]
        },
        [unit.id]
      );

  const privateFeatures = getFeaturesFromUnit(unit, [], PRIVATE_FEATURES[unit.type_code]);

  const area = isBed(unit.type_code) ? baseUnit.subunits.find(rootUnit => findBaseUnit(rootUnit, unitId)).area : null;

  const mainFeatures = getMainFeaturesFromUnit(unit, area);

  return {
    privateFeatures,
    commonFeatures,
    mainFeatures: [
      ...mainFeatures,
      ...Object.keys(privateFeatures).reduce(
        (acc, key) => [
          ...acc,
          ...privateFeatures[key]?.filter(feature => !!feature && !feature.translation.match(/bed/gi) && feature.exists)
        ],
        []
      )
    ]
  };
};

export const isSameBaseUnit = (unitA, unitB) => {
  if (!unitA.id || !unitB.id) {
    return true;
  }

  const isSameId = unitA.id === unitB.id;
  const isValidUnitRoot = ROOT_UNITS.includes(unitA.type_code);

  return isSameId && isValidUnitRoot;
};

export const getFeaturesFromUnit = (unit, excludedUnitIds = [], servicesFeatures = SERVICES_FEATURES) => {
  const getResults = () => {
    const areaFeature = {
      ...servicesFeatures.area,
      area: unit.area ?? 0,
      exists: !!unit.area
    };

    const unitFeatures = unit.features
      ? {
          [unit.type_code]: unit.features.reduce((acc, feature) => featuresReducer(acc, feature, servicesFeatures), [
            ...(areaFeature.exists ? [areaFeature] : [])
          ])
        }
      : {};

    if (!unit.subunits) {
      return isBed(unit.type_code) ? { bedroom: [UNIT_TYPE[unit.type_code]], ...unitFeatures } : { ...unitFeatures };
    }

    return unit.subunits.reduce(
      (acc, subunit) => {
        if (excludedUnitIds.includes(subunit.id)) {
          return acc;
        }

        const bedsFromBedroom =
          (subunit.type_code === 'bedroom' &&
            subunit.subunits?.filter(subunit => isBed(subunit.type_code)).map(bed => UNIT_TYPE[bed.type_code])) ||
          [];

        const beds = isBed(subunit.type_code) ? [UNIT_TYPE[subunit.type_code]] : [];

        return {
          ...acc,
          ...(acc.bedroom ? { bedroom: [...beds, ...acc.bedroom] } : {}),
          [subunit.type_code]: [
            ...bedsFromBedroom,
            ...(subunit.features
              ? subunit.features.reduce((acc, feature) => featuresReducer(acc, feature, servicesFeatures), [])
              : [])
          ]
        };
      },
      { ...unitFeatures }
    );
  };
  const results = getResults();

  return {
    ...results,
    [unit.type_code]:
      results?.[unit.type_code]?.sort((featureA, featureB) => {
        if (featureA.translation === featureB.translation) {
          return 0;
        }

        const featureOrder = { [featureA.translation]: -1, [featureB.translation]: 1 };

        return featureOrder[servicesFeatures.area.translation] ?? 0;
      }) || []
  };
};

export const getMainFeaturesFromUnit = (unit, areaFromParent = null) => {
  const area = {
    ...SERVICES_FEATURES['area'],
    area: areaFromParent ? areaFromParent : unit.area,
    exists: areaFromParent ? !!areaFromParent : !!unit.area
  };

  if (unit.type_code === 'bedroom') {
    const { type_code: bedType } = unit.subunits && unit.subunits.find(subunit => isBed(subunit.type_code));
    const privateWcOrBathroom = unit.subunits && unit.subunits.find(subunit => isWcOrBathroom(subunit.type_code));

    return privateWcOrBathroom
      ? [
          ...(area.area ? [area] : []),
          UNIT_TYPE[bedType],
          SERVICES_FEATURES[`private-${privateWcOrBathroom.type_code}`]
        ]
      : [...(area.area ? [area] : []), UNIT_TYPE[bedType]];
  }

  return [...(area.area ? [area] : [])];
};

export const getPropertyTitle = ({ offers }) => {
  return offers.find(offer => offer.property.title)?.property.title;
};

export const getRestrictions = restrictions => {
  if (!restrictions) {
    return [];
  }

  return restrictions.map(restriction => ({
    code: RESTRICTIONS_CODES_MAPPER[restriction],
    exists: false
  }));
};

export const restrictionsReducer = restrictions =>
  restrictions.reduce((acc, restriction) => {
    const converted = !restriction.exists && PROPERTY_RULES[restriction.code];
    if (!converted) {
      return acc;
    }

    return [...acc, converted];
  }, []);

export const transformFeatures = (features, filterExists) =>
  !features?.length
    ? []
    : features?.reduce((acc, { Code, Exists }) => {
        const featureKey = Object.keys(SERVICES_FEATURES).find(serviceFeature => serviceFeature === Code);
        if (!featureKey || (filterExists && !Exists)) {
          return acc;
        }

        const converted = getFeatureWithAssertedIcon({ Exists, Code: featureKey }, SERVICES_FEATURES);

        return [...acc, converted];
      }, []);
